import React, { useState, useCallback, useEffect } from "react";
import Waveform from "components/Trackwav/TrackWav";
import "./Listen.css";
import Tabs from "components/Tab/Tab";
import Button from "components/button/Button";
import { BASE_URL } from "apiUtil";

const DEMO_FILES = ["mix", "loud_demo", "stream_demo"];

function getTimeToMidnightEST() {
  const now = new Date();

  // Get current time in EST
  const estOffset = -5 * 60; // EST is UTC-5
  const utcNow = new Date(now.getTime() + now.getTimezoneOffset() * 60 * 1000);
  const estNow: any = new Date(utcNow.getTime() + estOffset * 60 * 1000);

  // Set EST midnight
  const midnight: any = new Date(estNow);
  midnight.setHours(24, 0, 0, 0); // Set to midnight of the same day

  // Calculate time difference in milliseconds
  const timeToMidnight = midnight - estNow;

  // Convert to hours and minutes
  const hours = Math.floor(timeToMidnight / (1000 * 60 * 60));
  const minutes = Math.floor((timeToMidnight % (1000 * 60 * 60)) / (1000 * 60));

  return `Track will expire in ${hours} hrs ${minutes} mins`;
}

const TRACK_INFO = [
  "This is a mixdown that was uploaded and includes a 30-second demo clip.",
  `Mastering at -11 dBFS is considered loud, pushing tracks closer to commercial loudness standards. 
  While it delivers impactful audio, it increases the risk of distortion if dynamics are overcompressed or levels clip. 
  Careful limiting and monitoring are essential to retain clarity and avoid compromising audio quality during loud mastering.`,
  `Mastering at -14 dBFS is tailored for streaming platforms, aligning with their loudness normalization standards. 
  This approach ensures consistent playback quality across devices while preserving dynamic range. It avoids the risk of distortion or clipping, 
  providing a clean and balanced sound optimized for modern streaming services without sacrificing audio integrity.`,
];

export const ListenPage = (props: any) => {
  const { projectID, trackName } = props;
  const [currentPlaying, setCurrentPlaying] = useState<any | null>(null); // Track the current WaveSurfer instance
  const [currentTime, setCurrentTime] = useState(0); // Track current time
  const [audioURL, setAudioURL] = useState<string | null>(null); // Current audio URL
  const [loading, setLoading] = useState(false); // Loading state for audio fetch
  const [masteringInfo, setMasteringInfo] = useState(TRACK_INFO[0]);
  const [tabIndex, setTableIndex] = useState(0);
  const handlePlay = (waveSurferInstance: any) => {
    if (currentPlaying && currentPlaying !== waveSurferInstance) {
      currentPlaying.stop(); // Stop the currently playing track
    }
    setCurrentPlaying(waveSurferInstance); // Set the new playing instance
  };

  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time); // Update current time
  }, []);

  const fetchAudio = async (type: string) => {
    setLoading(true);
    try {
      const response = await fetch(BASE_URL + "/prod/api/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projID: projectID,
          type,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch audio");
      }

      // Set the audio URL for playback
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioURL(audioUrl);
    } catch (error) {
      console.error("Error fetching audio:", error);
      setAudioURL(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTabSelect = (index: number) => {
    const selectedType = DEMO_FILES[index];
    setMasteringInfo(TRACK_INFO[index]);
    setTableIndex(index);
    fetchAudio(selectedType); // Fetch audio from the backend
    if (currentPlaying) {
      currentPlaying.stop(); // Stop the current track when switching
    }
    setCurrentPlaying(null); // Reset the current playing track
  };

  // Load "mix" audio on component mount
  useEffect(() => {
    fetchAudio("mix");
  }, []);

  const handleMp3Download = async () => {
    try {
      const projID = projectID; // Replace with the actual project ID
      const type = DEMO_FILES[tabIndex].split("_")[0]; // Replace with the actual type (e.g., "Mix", "Loud", or "Stream")
      const response = await fetch(BASE_URL + "/prod/api/download/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projID, type }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Error: ${errorData.error}`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${trackName.split(".")[0]}_${type}_master.mp3`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download the MP3 file.");
    }
  };
  return (
    <div>
      <div>
        <Tabs
          tabs={["Mix", "Loud", "Stream"]}
          onTabSelect={handleTabSelect}
          trackName={trackName.replace(/\.[^/.]+$/, "")}
        />
      </div>
      <div className="listen-wav-container shadow">
        {loading ? (
          <div>Loading...</div>
        ) : audioURL ? (
          <Waveform
            audioUrl={audioURL}
            onTimeUpdate={handleTimeUpdate}
            onPlay={handlePlay}
          />
        ) : (
          <div>No audio available</div>
        )}
      </div>
      <div className="listen-info-wrapper shadow">
        <div className="listen-info">{masteringInfo}</div>
        <div className="listen-download">
          {tabIndex !== 0 && (
            <>
              <Button
                variant="primary"
                label="Download .mp3"
                onClick={handleMp3Download}
              />
              {/* <Button variant="secondary" label="Download .wav" /> */}
              <div className="expire-info">{getTimeToMidnightEST()}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
